import { Delete02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/components/ui/toast";
import { api, ApiError, type CaseSummary } from "@/lib/api";
import { statusBadgeVariant } from "@/lib/case-format";
import { timezoneItems } from "@/lib/timezones";

export function SettingsForm({
  caseId,
  caseRow,
  hasPublishedVersion,
}: {
  caseId: string;
  caseRow: CaseSummary;
  hasPublishedVersion: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [timezone, setTimezone] = useState(caseRow.timezone);
  const [sortOrder, setSortOrder] = useState(String(caseRow.sortOrder));
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    setTimezone(caseRow.timezone);
    setSortOrder(String(caseRow.sortOrder));
  }, [caseRow.timezone, caseRow.sortOrder, caseRow.id]);

  const mutation = useMutation({
    mutationFn: () => {
      const order = Number(sortOrder);
      if (!Number.isFinite(order)) {
        throw new ApiError(400, "invalid_sort_order");
      }
      return api.patchCase(caseId, {
        timezone: timezone.trim() || undefined,
        sortOrder: order,
      });
    },
    onSuccess: async () => {
      setError(null);
      toast.add({
        type: "success",
        title: "设置已保存",
        description: "案件设置已更新。",
      });
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "save_failed";
      setError(code);
      toast.add({
        type: "error",
        title: "保存失败",
        description: code,
        priority: "high",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteCase(caseId),
    onSuccess: async () => {
      setDeleteOpen(false);
      toast.add({
        type: "success",
        title: "案件已删除",
        description: `已移除 ${caseId}。`,
      });
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.removeQueries({ queryKey: ["case", caseId] });
      void navigate({ to: "/" });
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "delete_failed";
      const detail =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      setError(detail);
      toast.add({
        type: "error",
        title: "删除失败",
        description: detail,
        priority: "high",
      });
    },
  });

  const dirty =
    timezone.trim() !== caseRow.timezone ||
    Number(sortOrder) !== caseRow.sortOrder;

  const tzItems = timezoneItems(timezone);
  const busy = mutation.isPending || deleteMutation.isPending;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>案件设置</CardTitle>
          <CardDescription>
            仅案件级字段。包标题来自版本元信息，不在此表单中。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="case-id">案件 ID</FieldLabel>
              <Input id="case-id" value={caseRow.id} readOnly disabled />
              <FieldDescription>不可变的案件标识符。</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="case-timezone">时区</FieldLabel>
              <Combobox
                items={tzItems}
                value={timezone || null}
                onValueChange={(next) => {
                  if (typeof next === "string") setTimezone(next);
                }}
                inputValue={timezone}
                onInputValueChange={(next) => setTimezone(next)}
                disabled={busy}
              >
                <ComboboxInput
                  id="case-timezone"
                  placeholder="America/Los_Angeles"
                  disabled={busy}
                  showClear={false}
                />
                <ComboboxContent>
                  <ComboboxEmpty>
                    无匹配项。请继续输入完整的 IANA 时区。
                  </ComboboxEmpty>
                  <ComboboxList>
                    {(item) => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </Field>
            <Field>
              <FieldLabel>状态</FieldLabel>
              <div className="flex items-center gap-2">
                <Badge variant={statusBadgeVariant(caseRow.status)}>
                  {caseRow.status}
                </Badge>
              </div>
              <FieldDescription>
                由发布状态自动决定：无线上语言为 draft，有线上语言为
                published。
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="case-sort">排序</FieldLabel>
              <Input
                id="case-sort"
                type="number"
                value={sortOrder}
                disabled={busy}
                onChange={(e) => setSortOrder(e.target.value)}
                className="max-w-xs"
              />
              <FieldDescription>
                数字越小，在案件列表中越靠前。
              </FieldDescription>
            </Field>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                disabled={busy || !dirty}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? <Spinner /> : null}
                保存设置
              </Button>
            </div>
            {error ? <FieldError>{error}</FieldError> : null}
            {caseRow.title ? (
              <Alert>
                <AlertTitle>标题</AlertTitle>
                <AlertDescription>
                  当前显示标题为{" "}
                  <span className="font-medium">{caseRow.title}</span>
                  ，来自版本元信息。请在版本「元信息」页编辑。
                </AlertDescription>
              </Alert>
            ) : null}
          </FieldGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>危险操作</CardTitle>
          <CardDescription>
            永久删除此案件及其下所有版本、上传、流水线运行、素材和 R2
            对象。线上案件须先取消发布。
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {hasPublishedVersion ? (
            <Alert variant="destructive">
              <AlertTitle>线上状态无法删除</AlertTitle>
              <AlertDescription>
                此案件至少有一个已发布语言。请先在「版本」页取消发布所有线上版本，再重试。
              </AlertDescription>
            </Alert>
          ) : null}
          <AlertDialog
            open={deleteOpen}
            onOpenChange={(open) => {
              if (deleteMutation.isPending) return;
              setDeleteOpen(open);
            }}
          >
            <AlertDialogTrigger
              render={
                <Button
                  variant="destructive"
                  disabled={busy || hasPublishedVersion}
                  title={
                    hasPublishedVersion
                      ? "删除前请先取消发布线上语言"
                      : "删除案件"
                  }
                  onClick={() => setDeleteOpen(true)}
                />
              }
            >
              {deleteMutation.isPending ? (
                <Spinner />
              ) : (
                <HugeiconsIcon
                  icon={Delete02Icon}
                  data-icon="inline-start"
                />
              )}
              删除案件
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>删除案件 {caseId}？</AlertDialogTitle>
                <AlertDialogDescription>
                  将永久删除该案件、所有版本、上传、流水线数据、素材和 R2
                  包文件。此操作不可撤销。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteMutation.isPending}>
                  取消
                </AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                >
                  {deleteMutation.isPending ? <Spinner /> : null}
                  删除案件
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
